import {
  FullPageModule,
  PreviewPage,
  SectionTitle,
} from '../../../styles/global.styles';
import {
  ComparableAddress,
  ComparableDetail,
  ComparableId,
  ComparableImage,
  ComparableItem,
  ComparableMainPrice,
  ComparablePrices,
  ComparablePricesBeaRequest,
  ComparablePrimaryLabel,
  ComparablePublishingChip,
  ComparablePublishingSection,
  ComparablesContainer,
  ComparablesDetails,
  ComparableSecondaryLabel,
  ComparablesFeatures,
  ComparablesInfo,
  ComparablesList,
  ComparableSubtypology,
  ComparableTitle,
  FeatureSeparator,
} from './comparables.styles';
import {
  getColorByComparisonType,
  getRetailListingLabel,
  isNotariadoSource,
  numberFormat,
  splitArrayIntoChunks,
} from '../../../helpers/utils';
import { formatCurrencyExtended } from '../../../helpers/currencyFormatter';
import { getFloorCategoryKey } from '../../../helpers/utils';
import {
  AssetDetailsIconDictionary,
  AssetFeaturesIconDictionary,
  PublishingStatusDictionary,
} from '../../../helpers/dictionaries.helper';
import ImagePlaceholder from '../../../assets/images/img-placeholder.png';
import { PageModulesContainer } from '../../../pages/assetReport/assetReport.styles';
import { AssetReportHeader } from '../ReportHeader/header.module';
import { AssetReportFooter } from '../ReportFooter/assetReportFooter.component';
import { CSSProperties, useState } from 'react';
import { ReportDataResponse } from '../../../types/reportData.interface';
import {
  Asset,
  ComparablesDataModel,
} from '../../../types/comparables.interfaces';
import { calculateBoundingBox } from '../../../helpers/internUtils';
import { calculateZoomLevel } from '../../../helpers/internUtils';
import { getCorrectPrice } from '../../../helpers/priceHelpers';
import { useIntl } from 'react-intl';

export function Comparables({
  comparablesData,
  reportData,
  listingType,
  operation,
  country = 'es',
  useCorrectedPrice = false,
}: {
  comparablesData: ComparablesDataModel;
  reportData: ReportDataResponse;
  listingType: number;
  operation: number;
  country?: string;
  useCorrectedPrice?: boolean;
}) {
  const intl = useIntl();
  const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN; // Replace with your actual token
  const mapStyle = 'pulse-dev/clq29uvkb00aa01qwhxdq3jx2'; // You can customize this with any Mapbox style
  const staticMapSize = '950x400'; // Static map size
  const itemsPerPage = 8;

  // Get translated suffixes
  const sqmSuffix = intl.formatMessage({ id: 'sqmPriceSuffix' });
  const sqmSuffix2 = intl.formatMessage({ id: 'sqmSuffix' });

  const comparablesToShow = comparablesData.comparables ?? null;

  const comparablesToShowWithId = comparablesToShow?.map(
    (comparable, index) => ({
      ...comparable,
      id: index + 1,
    })
  );

  /**
   * Planta del comparable. Notariado no da la planta exacta sino uno de tres tramos
   * (`cat_floor`), así que se usa ese tramo solo cuando no viene `floor`: igual que en la
   * card y el detalle de Pulse, donde el dato exacto siempre manda.
   */
  const getFloorLabel = (comparable: Asset) => {
    if (comparable.floor !== null && comparable.floor !== undefined) {
      return comparable.floor;
    }

    const labelId = getFloorCategoryKey(comparable.cat_floor);

    return labelId ? intl.formatMessage({ id: labelId }) : null;
  };

  /**
   * "Vendido" (comparison_type=2) no es correcto para Notariado: son transacciones
   * notariales, no publicaciones que se vendieron. Mientras Data no ajusta el dato en
   * origen, se corrige aqui solo para esa fuente.
   */
  const getPublishingStatusLabel = (comparable: Asset) => {
    const status = comparable.comparison_type;
    const validStatuses = Object.keys(PublishingStatusDictionary).map(Number);
    if (!validStatuses.includes(Number(status))) {
      return null;
    }

    if (Number(status) === 2 && isNotariadoSource(comparable.retail_listing)) {
      return intl.formatMessage({ id: 'publishingStatus.notariadoTransaction' });
    }

    return intl.formatMessage({
      id: `publishingStatus.${status}`,
    });
  };

  const comparableChunks = splitArrayIntoChunks(
    comparablesToShowWithId ?? [],
    itemsPerPage
  );

  const maxDistance = Math.max(
    ...(comparablesToShowWithId?.map(
      (comparable: Asset) => comparable.distance
    ) ?? [])
  );

  const zoomLevel = calculateZoomLevel(maxDistance);

  // Create the center marker
  const centerMarker = `pin-l+FF0000(${Number(comparablesData.lng).toFixed(
    4
  )},${Number(comparablesData.lat).toFixed(4)})`; // Example: large red pin

  // Build the markers as part of the static map URL
  const markers = comparablesToShowWithId
    ?.map((marker) => {
      const color = getPublishingStatusChipColor(marker.comparison_type);
      return `pin-l-${marker.id}+${color?.backgroundColor.slice(1)}(${
        marker.location.coordinates[0]
      },${marker.location.coordinates[1]})`;
    })
    .join(',');

  const allMarkers = markers ? [markers, centerMarker].join(',') : centerMarker;

  // Create the URL for the static map image
  const staticMapUrl = `https://api.mapbox.com/styles/v1/${mapStyle}/static/${allMarkers}/${Number(
    comparablesData.lng
  ).toFixed(6)},${Number(comparablesData.lat).toFixed(
    6
  )},${zoomLevel}/${staticMapSize}?access_token=${MAPBOX_ACCESS_TOKEN}`;

  return (
    <>
      <PreviewPage>
        <PageModulesContainer>
          <AssetReportHeader
            data={reportData}
            listingType={listingType}
            reportType={null}
            operation={operation}
          />
          <FullPageModule>
            <SectionTitle>Comparables</SectionTitle>
            <ComparablesContainer>
              {/* Replace WebGL Mapbox with Static Map */}
              {staticMapUrl && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={staticMapUrl}
                    alt="Static Map"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '8px',
                    }}
                  />
                </div>
              )}
              <ComparablesList>
                {comparableChunks &&
                  comparableChunks[0].map((comparable, index) => (
                    <ComparableItem key={index + 1}>
                      <ComparableId
                        color={
                          getPublishingStatusChipColor(
                            comparable.comparison_type
                          )?.backgroundColor
                        }
                        href={`${process.env.REACT_APP_PULSE_URL}/asset/detail/${comparable._id}?country=${country}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {comparable.id}
                      </ComparableId>
                      {getImageToShow(comparable) ? (
                        <ComparableImage>
                          <ComparableImageWithFallback
                            src={getImageToShow(comparable)!.url}
                            label={getImageToShow(comparable)!.label}
                            fallbackSrc={ImagePlaceholder}
                            alt={`Image for ${comparable.id}`}
                          />
                        </ComparableImage>
                      ) : (
                        <ComparableImage>
                          <img
                            src={ImagePlaceholder}
                            alt={`Image for ${comparable.id}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                            }}
                          />
                        </ComparableImage>
                      )}
                      <ComparablesInfo>
                        <ComparableTitle>
                          <ComparableSubtypology>
                            {intl.formatMessage({
                              id: `subtypology.${comparable.property_type_id}`,
                            })}
                          </ComparableSubtypology>
                          <FeatureSeparator />
                          <ComparableAddress>
                            {comparable.address ??
                              comparable.admin5 ??
                              comparable.admin4 ??
                              '-'}
                          </ComparableAddress>
                        </ComparableTitle>
                        <ComparablesDetails>
                          <ComparableDetail>
                            {AssetDetailsIconDictionary.area}
                            <ComparablePrimaryLabel>
                              {comparable.area} {sqmSuffix2}
                            </ComparablePrimaryLabel>
                          </ComparableDetail>
                          {getFloorLabel(comparable) !== null && (
                            <ComparableDetail>
                              {AssetDetailsIconDictionary.floor}
                              <ComparablePrimaryLabel>
                                {getFloorLabel(comparable)}
                              </ComparablePrimaryLabel>
                            </ComparableDetail>
                          )}
                          {comparable.n_rooms !== null &&
                            comparable.n_rooms !== undefined && (
                              <ComparableDetail>
                                {AssetDetailsIconDictionary.bedrooms}
                                <ComparablePrimaryLabel>
                                  {comparable.n_rooms ?? '-'}
                                </ComparablePrimaryLabel>
                              </ComparableDetail>
                            )}
                          {comparable.n_baths !== null &&
                            comparable.n_baths !== undefined && (
                              <ComparableDetail>
                                {AssetDetailsIconDictionary.bathrooms}
                                <ComparablePrimaryLabel>
                                  {comparable.n_baths ?? '-'}
                                </ComparablePrimaryLabel>
                              </ComparableDetail>
                            )}
                          <FeatureSeparator />
                          {listingType !== 8 && (
                            <ComparableDetail>
                              <ComparableSecondaryLabel>
                                {intl.formatMessage({
                                  id: 'comparables.year_built',
                                })}
                              </ComparableSecondaryLabel>
                              {comparable.construction_year !== null &&
                              comparable.construction_year !== undefined ? (
                                <ComparablePrimaryLabel>
                                  {comparable.construction_year}
                                </ComparablePrimaryLabel>
                              ) : (
                                <ComparablePrimaryLabel>
                                  -
                                </ComparablePrimaryLabel>
                              )}
                            </ComparableDetail>
                          )}
                        </ComparablesDetails>
                        <ComparablesFeatures>
                          {comparable.assetFeatures &&
                          Object.values(comparable.assetFeatures).some(
                            (value) => value === 1
                          ) ? (
                            <>
                              {Object.entries(comparable.assetFeatures).map(
                                (feature) => {
                                  if (feature[1] === 1) {
                                    return AssetFeaturesIconDictionary[
                                      feature[0] as keyof typeof AssetFeaturesIconDictionary
                                    ];
                                  }
                                  return null;
                                }
                              )}
                              <FeatureSeparator />
                            </>
                          ) : (
                            <></>
                          )}
                          <ComparableSecondaryLabel>
                            {intl.formatMessage({ id: 'comparables.distance' })}
                          </ComparableSecondaryLabel>
                          <ComparablePrimaryLabel>
                            {numberFormat.format(
                              parseFloat(Number(comparable.distance).toFixed(0))
                            )}
                            m
                          </ComparablePrimaryLabel>
                          <FeatureSeparator />
                          <ComparablePrimaryLabel>
                            {getRetailListingLabel(comparable.retail_listing) ??
                              '-'}
                          </ComparablePrimaryLabel>
                          <FeatureSeparator />
                          <ComparableSecondaryLabel>
                            {getFormattedDate(comparable.date_main.$date)}
                          </ComparableSecondaryLabel>
                        </ComparablesFeatures>
                      </ComparablesInfo>
                      <ComparablePrices>
                        <ComparablePricesBeaRequest>
                          <ComparableMainPrice>
                           {formatCurrencyExtended(
                                getCorrectPrice(
                                  comparable,
                                  true,
                                  useCorrectedPrice
                                ),
                                country,
                                false,
                                sqmSuffix,
                                undefined,
                                true,
                                1000000000
                              )}
                          </ComparableMainPrice>
                          <ComparableSecondaryLabel>
                            {isNaN(
                              Number(
                                getCorrectPrice(
                                  comparable,
                                  false,
                                  useCorrectedPrice
                                )
                              )
                            )
                              ? '-'
                              : formatCurrencyExtended(
                                  getCorrectPrice(
                                    comparable,
                                    false,
                                    useCorrectedPrice
                                  ),
                                  country,
                                  true,
                                  sqmSuffix,
                                  undefined,
                                  true
                                )}
                          </ComparableSecondaryLabel>
                        </ComparablePricesBeaRequest>
                        <ComparablePublishingSection>
                          <ComparablePublishingChip
                            backgroundcolor={
                              (comparable.comparison_type !== null &&
                                comparable.comparison_type !== undefined &&
                                getPublishingStatusChipColor(
                                  comparable.comparison_type
                                )?.backgroundColor) ||
                              '#FFFFFF'
                            }
                            color={
                              (comparable.comparison_type !== null &&
                                comparable.comparison_type !== undefined &&
                                getPublishingStatusChipColor(
                                  comparable.comparison_type
                                )?.color) ||
                              '#FFFFFF'
                            }
                          >
                            {getPublishingStatusLabel(comparable)}
                          </ComparablePublishingChip>
                        </ComparablePublishingSection>
                      </ComparablePrices>
                    </ComparableItem>
                  ))}
              </ComparablesList>
            </ComparablesContainer>
          </FullPageModule>
          <AssetReportFooter {...reportData} />
        </PageModulesContainer>
      </PreviewPage>
      {comparableChunks &&
        comparableChunks.length > 1 &&
        comparableChunks.slice(1).map((chunk, index) => (
          <PreviewPage key={index}>
            <PageModulesContainer>
              <AssetReportHeader
                data={reportData}
                listingType={listingType}
                reportType={null}
                operation={operation}
              />
              <FullPageModule>
                <ComparablesContainer>
                  <ComparablesList>
                    {chunk.map((comparable, index) => (
                      <ComparableItem key={comparable.id}>
                        <ComparableId
                          color={
                            getPublishingStatusChipColor(
                              comparable.comparison_type
                            )?.backgroundColor
                          }
                          href={`${process.env.REACT_APP_PULSE_URL}/asset/detail/${comparable._id}?country=${country}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {comparable.id}
                        </ComparableId>
                        {getImageToShow(comparable) ? (
                          <ComparableImage>
                            <ComparableImageWithFallback
                              src={getImageToShow(comparable)!.url}
                              alt={`Image for ${comparable.id}`}
                              fallbackSrc={ImagePlaceholder}
                              label={getImageToShow(comparable)!.label}
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '8px',
                              }}
                            />
                          </ComparableImage>
                        ) : (
                          <ComparableImage>
                            <img
                              src={ImagePlaceholder}
                              alt={`Image for ${comparable.id}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '8px',
                              }}
                            />
                          </ComparableImage>
                        )}
                        <ComparablesInfo>
                          <ComparableTitle>
                            <ComparableSubtypology>
                              {intl.formatMessage({
                                id: `subtypology.${comparable.property_type_id}`,
                              })}
                            </ComparableSubtypology>
                            <FeatureSeparator />
                            <ComparableAddress>
                              {comparable.address ??
                                comparable.admin5 ??
                                comparable.admin4 ??
                                '-'}
                            </ComparableAddress>
                          </ComparableTitle>
                          <ComparablesDetails>
                            <ComparableDetail>
                              {AssetDetailsIconDictionary.area}
                              <ComparablePrimaryLabel>
                                {comparable.area ?? '-'}
                                {sqmSuffix}
                              </ComparablePrimaryLabel>
                            </ComparableDetail>
                            {getFloorLabel(comparable) !== null && (
                              <ComparableDetail>
                                {AssetDetailsIconDictionary.floor}
                                <ComparablePrimaryLabel>
                                  {getFloorLabel(comparable)}
                                </ComparablePrimaryLabel>
                              </ComparableDetail>
                            )}
                            {comparable.n_rooms !== null &&
                              comparable.n_rooms !== undefined && (
                                <ComparableDetail>
                                  {AssetDetailsIconDictionary.bedrooms}
                                  <ComparablePrimaryLabel>
                                    {comparable.n_rooms}
                                  </ComparablePrimaryLabel>
                                </ComparableDetail>
                              )}
                            {comparable.n_baths !== null &&
                              comparable.n_baths !== undefined && (
                                <ComparableDetail>
                                  {AssetDetailsIconDictionary.bathrooms}
                                  <ComparablePrimaryLabel>
                                    {comparable.n_baths}
                                  </ComparablePrimaryLabel>
                                </ComparableDetail>
                              )}
                            <FeatureSeparator />
                            {listingType !== 8 && (
                              <ComparableDetail>
                                <ComparableSecondaryLabel>
                                  {intl.formatMessage({
                                    id: 'comparables.year_built',
                                  })}
                                </ComparableSecondaryLabel>
                                {comparable.construction_year !== null &&
                                comparable.construction_year !== undefined ? (
                                  <ComparablePrimaryLabel>
                                    {comparable.construction_year}
                                  </ComparablePrimaryLabel>
                                ) : (
                                  <ComparablePrimaryLabel>
                                    -
                                  </ComparablePrimaryLabel>
                                )}
                              </ComparableDetail>
                            )}
                          </ComparablesDetails>
                          <ComparablesFeatures>
                            {comparable.assetFeatures &&
                            Object.values(comparable.assetFeatures).some(
                              (value) => value === 1
                            ) ? (
                              <>
                                {Object.entries(comparable.assetFeatures).map(
                                  (feature) => {
                                    if (feature[1] === 1) {
                                      return AssetFeaturesIconDictionary[
                                        feature[0] as keyof typeof AssetFeaturesIconDictionary
                                      ];
                                    }
                                    return null;
                                  }
                                )}
                                <FeatureSeparator />
                              </>
                            ) : (
                              <></>
                            )}
                            <ComparableSecondaryLabel>
                              {intl.formatMessage({
                                id: 'comparables.distance',
                              })}
                            </ComparableSecondaryLabel>
                            <ComparablePrimaryLabel>
                              {numberFormat.format(
                                parseFloat(
                                  Number(comparable.distance).toFixed(0)
                                )
                              )}
                              m
                            </ComparablePrimaryLabel>
                            <FeatureSeparator />
                            <ComparablePrimaryLabel>
                              {getRetailListingLabel(comparable.retail_listing) ??
                                '-'}
                            </ComparablePrimaryLabel>
                            <FeatureSeparator />
                            <ComparableSecondaryLabel>
                              {getFormattedDate(comparable.date_main.$date)}
                            </ComparableSecondaryLabel>
                          </ComparablesFeatures>
                        </ComparablesInfo>
                        <ComparablePrices>
                          <ComparablePricesBeaRequest>
                            <ComparableMainPrice>
                              {formatCurrencyExtended(
                                getCorrectPrice(
                                  comparable,
                                  true,
                                  useCorrectedPrice
                                ),
                                country,
                                false,
                                sqmSuffix,
                                undefined,
                                true,
                                1000000000
                              )}
                            </ComparableMainPrice>
                            <ComparableSecondaryLabel>
                              {isNaN(
                                Number(
                                  getCorrectPrice(
                                    comparable,
                                    false,
                                    useCorrectedPrice
                                  )
                                )
                              )
                                ? '-'
                                : formatCurrencyExtended(
                                    getCorrectPrice(
                                      comparable,
                                      false,
                                      useCorrectedPrice
                                    ),
                                    country,
                                    true,
                                    sqmSuffix,
                                    undefined,
                                    true,
                                    1000000000
                                  )}
                            </ComparableSecondaryLabel>
                          </ComparablePricesBeaRequest>
                          <ComparablePublishingSection>
                            <ComparablePublishingChip
                              backgroundcolor={
                                (comparable.comparison_type !== null &&
                                  comparable.comparison_type !== undefined &&
                                  getPublishingStatusChipColor(
                                    comparable.comparison_type
                                  )?.backgroundColor) ||
                                '#FFFFFF'
                              }
                              color={
                                (comparable.comparison_type !== null &&
                                  comparable.comparison_type !== undefined &&
                                  getPublishingStatusChipColor(
                                    comparable.comparison_type
                                  )?.color) ||
                                '#FFFFFF'
                              }
                            >
                              {getPublishingStatusLabel(comparable)}
                            </ComparablePublishingChip>
                          </ComparablePublishingSection>
                        </ComparablePrices>
                      </ComparableItem>
                    ))}
                  </ComparablesList>
                </ComparablesContainer>
              </FullPageModule>
              <AssetReportFooter {...reportData} />
            </PageModulesContainer>
          </PreviewPage>
        ))}
    </>
  );
}

function getFormattedDate(publishingDate: string) {
  const date = new Date(publishingDate);

  // Format the date to dd-MM-YYYY
  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return formattedDate;
}

function getImageToShow(comparable: Asset) {
  if (comparable.images && comparable.images.length > 0) {
    const imageSource = comparable.images[0];
    const transformedUrl = imageSource.url.replace(
      /\/[^\/]*WEB_DETAIL[^\/]*\//,
      '/480_360_mq/'
    );
    return {
      ...imageSource,
      url: transformedUrl,
    };
  }
  return null;
}

function getPublishingStatusChipColor(id: number) {
  if (id == 1) {
    return {
      backgroundColor: '#D7ECEA',
      color: '#09332C',
    };
  } else if (id === 0) {
    return {
      backgroundColor: '#EFDDE3',
      color: '#512B58',
    };
  } else {
    return {
      backgroundColor: '#F2DBC8',
      color: '#B56820',
    };
  }
}

const ComparableImageWithFallback = ({
  src,
  label,
  fallbackSrc,
  alt,
  style,
}: {
  src: string;
  label: string;
  fallbackSrc: string;
  alt: string;
  style?: CSSProperties;
}) => {
  const [currentSrc, setCurrentSrc] = useState(addPrefixToLastSlug(src, label));
  const [hasError, setHasError] = useState(false);

  const handleImageError = () => {
    if (!hasError && fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setHasError(true);
    }
  };

  return <img src={currentSrc} alt={alt} onError={handleImageError} />;
};

function addPrefixToLastSlug(url: string, label: string) {
  // Verificar que url no sea null, undefined o vacío
  if (!url || typeof url !== 'string') {
    return url || '';
  }

  if (label === 'catastro') {
    return url;
  }

  // let parts = url.split('/');

  // let lastSlug = parts.pop();

  // let modifiedSlug = 'tb_' + lastSlug;

  // let modifiedUrl = parts.join('/') + '/' + modifiedSlug;

  // return modifiedUrl;
  return url;
}
